import React, { useEffect, useRef, useState } from "react";
import { TextInput, Loader } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";

/**
 * Staff-typed custom code field for a gift card. Used in three flows so they all
 * validate + probe a code identically: selling a card (dedicated sale modal or a
 * transaction line item, where the code is OPTIONAL — blank auto-generates), and
 * editing a held card's code (`EditGiftCardCodeModal`, where the code is
 * REQUIRED — pass `required`).
 *
 * Client-side format mirrors the backend `validate_custom_code`
 * (`gift_card_utils.code_generation`): alphanumeric, 4–8 characters. Availability
 * is checked (debounced) against the core `code-available` route — a UX hint
 * only, NOT a lock: the real guarantee is the backend's write-time re-check, so a
 * transient check failure never blocks submission.
 *
 * Pass `currentCode` when editing a card's own code so the field treats that
 * value as available (a card never collides with itself) instead of probing and
 * flagging it as taken.
 */

/** Alphanumeric, 4–8 chars — the client mirror of `validate_custom_code`. */
const CODE_FORMAT = /^[A-Za-z0-9]{4,8}$/;
const AVAILABILITY_DEBOUNCE_MS = 400;

type Availability = "idle" | "checking" | "available" | "taken" | "error";

interface GiftCardCodeFieldProps {
  /** Salon UUID (businessInfo.id) — routed to the /business-locations route. */
  salonId: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Reports whether the current code is OK to submit: a well-formed code whose
   * availability probe has RESOLVED to available (or, when not `required`, an
   * empty value that auto-generates). Reports `false` for a malformed or
   * known-taken code, an empty value when `required`, AND while the probe is
   * still in flight — so a gated "next"/submit button stays disabled until the
   * code is confirmed. A transient probe error reports `true` (the backend is the
   * authority at write time). Keep the handler stable or accept it being called
   * on each validity change.
   */
  onValidityChange?: (isValid: boolean) => void;
  disabled?: boolean;
  label?: string;
  /**
   * When `true`, an empty value is INVALID (a code must be supplied). Used by the
   * edit-code flow; the sell flow leaves it `false` so blank auto-generates.
   */
  required?: boolean;
  /**
   * The card's own current code (edit flow). A value equal to it (case-
   * insensitive) is treated as available without probing, so the pre-filled code
   * isn't flagged as taken. Submit-gating on "actually changed" is the caller's
   * job.
   */
  currentCode?: string;
}

export default function GiftCardCodeField({
  salonId,
  value,
  onChange,
  onValidityChange,
  disabled = false,
  label = "Custom code (optional)",
  required = false,
  currentCode,
}: GiftCardCodeFieldProps) {
  const { checkGiftCardCodeAvailable } = useApp();
  const [availability, setAvailability] = useState<Availability>("idle");

  const trimmed = value.trim();
  const isEmpty = trimmed.length === 0;
  const formatValid = CODE_FORMAT.test(trimmed);
  const formatError =
    !isEmpty && !formatValid ? "Use 4–8 letters and numbers." : null;
  // The card's own code (edit flow) is available by definition — skip the probe.
  const isCurrentCode =
    !!currentCode && trimmed.toLowerCase() === currentCode.trim().toLowerCase();

  // Debounced availability probe — only for a non-empty, well-formed code that
  // isn't the card's own current code.
  useEffect(() => {
    if (isEmpty || !formatValid) {
      setAvailability("idle");
      return;
    }
    if (isCurrentCode) {
      setAvailability("available");
      return;
    }
    setAvailability("checking");
    let cancelled = false;
    const handle = setTimeout(() => {
      checkGiftCardCodeAvailable(salonId, trimmed)
        .then((available) => {
          if (!cancelled) setAvailability(available ? "available" : "taken");
        })
        .catch(() => {
          // Degrade gracefully: the backend re-checks at write time.
          if (!cancelled) setAvailability("error");
        });
    }, AVAILABILITY_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    salonId,
    trimmed,
    isEmpty,
    formatValid,
    isCurrentCode,
    checkGiftCardCodeAvailable,
  ]);

  // Report validity via a ref so an inline (non-memoized) handler can't loop.
  // A non-empty code is only OK to submit once the availability probe has
  // RESOLVED — `available` (confirmed free) or `error` (probe failed, so the
  // backend re-checks at write time). While it is still `checking`/`idle` the
  // code is NOT yet valid, so a gated "next" button can't fire mid-check.
  const isValid = isEmpty
    ? !required
    : formatValid && (availability === "available" || availability === "error");
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;
  useEffect(() => {
    onValidityChangeRef.current?.(isValid);
  }, [isValid]);

  const emptyDescription = required
    ? undefined
    : "Leave blank to auto-generate an 8-character code.";
  // The write-time authority differs by flow: a sold card is minted at payment,
  // an edited card is renamed on save.
  const errorDescription = required
    ? "Couldn't verify availability — it's confirmed when you save."
    : "Couldn't verify availability — the code is finalized at payment.";
  const description =
    availability === "taken"
      ? undefined
      : availability === "error"
        ? errorDescription
        : emptyDescription;

  const error =
    formatError ??
    (availability === "taken" ? "That code is already in use." : null);

  const rightSection =
    availability === "checking" ? (
      <Loader size="xs" color="var(--brand-teal)" />
    ) : availability === "available" ? (
      <IconCheck size={16} color="var(--mantine-color-green-6)" />
    ) : availability === "taken" ? (
      <IconX size={16} color="var(--mantine-color-red-6)" />
    ) : null;

  return (
    <TextInput
      label={label}
      placeholder="e.g. GIFT2026"
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      disabled={disabled}
      maxLength={8}
      error={error}
      description={description}
      rightSection={rightSection}
      rightSectionPointerEvents="none"
      autoComplete="off"
    />
  );
}
